import { Sidebar } from '../../components/sidebar';
import FlowFooter from '../../components/FlowFooter';
import LanguageSelector from '../../components/LanguageSelector';
import ClientLogo from '../../components/ClientLogo';
import MobileLayout from '../../layouts/MobileLayout';
import { useLayoutData } from '../../hooks/useLayoutContext';
import { useTranslation } from '../../i18n/i18n';

type Props = {
  singleApp: boolean;
  onContinue: () => void;
  onCancel: () => void;
};

/**
 * MobileFlowStartPage — Mobile entry page shown before app selection / Chulak flow.
 *
 * Displays sidebar content inline (title, description, steps, requested data, terms)
 * with a sticky CTA footer at the bottom. Uses MobileLayout with a custom header.
 *
 * Figma screens:
 *   Single app:    https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=176-22554
 *   Multiple apps: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=293-17552
 */
export default function MobileFlowStartPage({
  singleApp,
  onContinue,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const {
    client,
    requestType,
    interactionConfig,
    requestedDataGroups,
    requestedDataLoading,
  } = useLayoutData();

  const customHeader = (
    <header className="flex items-center justify-between border-b border-border bg-background px-4 py-3 shrink-0">
      {singleApp ? (
        <>
          <div className="flex flex-1 items-center justify-start">
            <ClientLogo client={client} />
          </div>
          <div className="flex items-center justify-end">
            <LanguageSelector compact />
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-1 items-center" />
          <div className="flex items-center justify-center shrink-0">
            <ClientLogo client={client} />
          </div>
          <div className="flex flex-1 items-center justify-end">
            <LanguageSelector compact />
          </div>
        </>
      )}
    </header>
  );

  return (
    <MobileLayout
      header={customHeader}
      contentClassName="bg-sidebar px-4"
      footer={
        <FlowFooter
          primaryLabel={t('mobileFlowStart.startVerification')}
          onPrimary={onContinue}
          secondaryLabel={t('agentStart.cancel')}
          onSecondary={onCancel}
        />
      }
    >
      <Sidebar
        client={client}
        requestType={requestType}
        interactionConfig={interactionConfig}
        requestedDataGroups={singleApp ? requestedDataGroups : undefined}
        requestedDataLoading={singleApp ? requestedDataLoading : false}
        variant="mobile"
      />
    </MobileLayout>
  );
}
